// Address search for Group settings (OTC-2926): maps Amazon Location Places v2
// results onto company_groups address fields. Pure functions, no network.
// AWS service terms 82.4(a): Autocomplete results are display only; the saved
// fields come from a Geocode requested with IntendedUse 'Storage'.

type PlacesAddress = {
  Label?: string
  AddressNumber?: string
  Street?: string
  Locality?: string
  PostalCode?: string
  Region?: { Code?: string; Name?: string }
  Country?: { Code2?: string; Code3?: string; Name?: string }
}

type PlacesItem = {
  Title?: string
  Address?: PlacesAddress
  Position?: [number, number]
}

type Country = {
  name: string
  code: string
  city_label: string
  state_label: string
  zip_label: string
  zip_placeholder: string
  phone_placeholder: string
}

type State = { name: string; code: string }

export type AddressSuggestion = {
  value: string
  line1: string
  line2: string
  label: string
  countryCode2: string
}

export type GeocodedAddress = {
  line1: string
  city: string
  regionCode: string
  regionName: string
  postalCode: string
  countryCode2: string
  latitude: number | null
  longitude: number | null
}

// company_groups column lengths (documents/db-schema-reference.md).
const MAX = { address_line_1: 300, city: 50, state: 20, zip: 10 }

function cut(value: string, max: number): string {
  return value.length > max ? value.slice(0, max) : value
}

function splitLabel(label: string): { first: string; rest: string } {
  const parts = label.split(',')
  return { first: parts[0].trim(), rest: parts.slice(1).join(',').trim() }
}

export function suggestionFromAutocomplete(item: PlacesItem): AddressSuggestion {
  const label = item.Address?.Label || item.Title || ''
  const { first, rest } = splitLabel(label)
  return { value: first, line1: first, line2: rest, label, countryCode2: item.Address?.Country?.Code2 || '' }
}

export function addressFromGeocode(item: PlacesItem): GeocodedAddress {
  const a = item.Address || {}
  const fromLabel = a.Label ? splitLabel(a.Label).first : ''
  const fromParts = [a.AddressNumber, a.Street].filter(Boolean).join(' ')
  const position = Array.isArray(item.Position) && item.Position.length === 2 ? item.Position : null
  return {
    line1: fromLabel || fromParts,
    city: a.Locality || '',
    regionCode: a.Region?.Code || '',
    regionName: a.Region?.Name || '',
    postalCode: a.PostalCode || '',
    countryCode2: a.Country?.Code2 || '',
    latitude: position ? position[1] : null,
    longitude: position ? position[0] : null,
  }
}

export function groupFieldsFromAddress(address: GeocodedAddress, countries: Country[], states: State[]) {
  const country = countries.find(c => c.code === address.countryCode2) || null
  let state: string
  if (country && country.state_label === 'State') {
    const match = states.find(s => s.code === address.regionCode || s.name === address.regionName)
    state = match ? match.name : ''
  } else {
    state = address.regionName.length > MAX.state && address.regionCode ? address.regionCode : address.regionName
  }
  return {
    address_line_1: cut(address.line1, MAX.address_line_1),
    city: cut(address.city, MAX.city),
    state: cut(state, MAX.state),
    zip: cut(address.postalCode, MAX.zip),
    country,
  }
}
